import type { AutoTopUpSettingsDto, CompanyBillingSummaryDto } from '@shared/services/apiService';
import { formatCreditUnits } from './formatBillingUnits';
export interface BillingAlert {
  key: string;
  severity: 'info' | 'warning';
  title: string;
  detail: string;
}
/** Current actionable states, not a synthetic event history. No purchase is triggered here. */
export function billingAlerts(
  summary: CompanyBillingSummaryDto,
  auto?: AutoTopUpSettingsDto,
): BillingAlert[] {
  if (summary.mode !== 'credit_managed') return [];
  const alerts: BillingAlert[] = [];
  if (summary.status === 'review')
    alerts.push({
      key: 'review',
      severity: 'warning',
      title: 'Billing needs attention',
      detail: 'New calls are paused. Review the billing hold or contact support.',
    });
  else if (summary.status === 'expired')
    alerts.push({
      key: 'expired',
      severity: 'warning',
      title: 'Your service period ended',
      detail: 'Choose or renew a paid plan to start new calls. Purchased credits are retained.',
    });
  else if (summary.plan && summary.balance && ['paid', 'trial'].includes(summary.status)) {
    const available = summary.totalAvailableUnits ?? summary.balance.availableUnits;
    const threshold =
      summary.status === 'trial'
        ? BigInt(summary.balance.grantedUnits) / 5n
        : BigInt(auto?.thresholdCredits ?? '100') * 10n ** BigInt(summary.plan.creditScale);
    if (/^\d+$/.test(available) && BigInt(available) <= threshold)
      alerts.push({
        key: 'low-balance',
        severity: 'warning',
        title: BigInt(available) === 0n ? 'No credits available' : 'Low credit balance',
        detail: `${formatCreditUnits(available, summary.plan.creditScale)} credits available. ${summary.status === 'paid' ? 'Add credits or review automatic top-up.' : 'Choose a paid plan to keep calling.'}`,
      });
  }
  if (auto?.enabled) {
    if (auto.paused)
      alerts.push({
        key: 'auto-paused',
        severity: 'warning',
        title: 'Automatic top-up is paused',
        detail: 'An active paid period and the approved package are required.',
      });
    const used = BigInt(auto.spentMinor) + BigInt(auto.reservedMinor),
      budget = BigInt(auto.budgetMinor);
    if (budget > 0n && used * 100n >= budget * 80n)
      alerts.push({
        key: 'auto-budget',
        severity: 'warning',
        title: used >= budget ? 'Automatic budget reached' : 'Automatic budget nearly used',
        detail:
          'Confirmed and pending purchases count toward this paid-period budget. Manual purchases remain separate.',
      });
  }
  if (auto?.pending?.needsAttention)
    alerts.push({
      key: 'payment-attention',
      severity: 'warning',
      title: 'An automatic payment needs attention',
      detail:
        'Review the pending purchase in Billing. Credits are added only after payment confirmation.',
    });
  return alerts;
}
